import React, { useEffect, useMemo, useState } from 'react';
import { RightOutlined, SettingOutlined } from '@ant-design/icons';
import {
  Button,
  Drawer,
  Space,
  Table,
  TablePaginationConfig,
  TableProps,
  Typography,
} from 'antd';
import { observer } from 'mobx-react-lite';

import {
  PageInfo,
  TConstraints,
} from 'app/common/constraints/core/constraints';
import { TColumn } from 'app/common/table-columns/core/columns';
import { hasRequiredRoles } from 'app/railways/domains/access/features/roles/has-required-roles';
import { TRoleNames } from 'app/railways/domains/access/features/roles/roles';
import { useUserRoles } from 'app/railways/domains/access/features/roles/use-user-roles';

import { Column } from './column';

import styles from './base-table.module.css';

const mergeWithPersistance = function <T>({
  columns,
  persistentId,
}: {
  columns?: Column<T>[];
  persistentId?: string;
}) {
  const params = window.localStorage.getItem(`${persistentId}/visibleColumns`);
  if (!persistentId || !columns || !params) return columns;

  const savedParams: { dataIndex: string; visible: boolean }[] =
    JSON.parse(params);

  const updatedColumns =
    columns &&
    columns.map((col) => {
      const colSetting = savedParams.find(
        (param) => param.dataIndex === col.dataIndex,
      );
      return colSetting ? { ...col, ...colSetting } : col;
    });

  return updatedColumns;
};

const filterResrictedRolesColumns = function <T>(
  columns: Column<T>[],
  userRoles: TRoleNames[],
) {
  const result = columns.filter(
    (col) => !hasRequiredRoles(userRoles, col.restrictedRoles),
  );

  return result;
};

const basePagination = {
  hideOnSinglePage: false,
  showSizeChanger: true,
  defaultPageSize: 40,
};

type TBasePagination = { current: number; pageSize: number };
export type TBaseConstraints<C extends TConstraints<any, any>> = {
  pagination: TBasePagination;
  sort: C['sorting'];
};

type BaseTableProps<
  T extends { id: number },
  C extends TConstraints<any, any>,
> = {
  paging?: PageInfo;
  rightPanel?: JSX.Element | JSX.Element[];
  hasSettings?: boolean;
  selectedTableRows?: Array<T>;
  onSelect?: (selected: Array<T>) => void;
  onChange?: (constraints: TBaseConstraints<C>) => void;
  onSort?: (sorts: { name: string; direction: 'descend' | 'ascend' }[]) => void;
  columns?: Column<T>[];
  persistentId?: string;
} & Omit<TableProps<T>, 'onChange'>;

const BaseTableComponent = observer(
  <
    T extends { id: number },
    C extends TConstraints<any, any> = TConstraints<any, any>,
  >({
    paging,
    onSelect,
    onChange,
    rightPanel,
    hasSettings = true,
    rowSelection,
    columns,
    expandable,
    persistentId,
    selectedTableRows = [],
    ...props
  }: BaseTableProps<T, C>) => {
    const userRoles = useUserRoles();
    const [visibleColumns, setVisibleColumns] = useState(() => {
      const permCols = mergeWithPersistance<T>({ columns, persistentId }) || [];
      const restrictedRolesCols = filterResrictedRolesColumns<T>(
        permCols,
        userRoles,
      );
      const finalCols = restrictedRolesCols;

      return finalCols;
    });
    const [settingsVisible, setSettingsVisible] = useState(false);
    const [selectedRows, setSelectedRows] =
      React.useState<Array<T>>(selectedTableRows);

    const rowSelectionParams = React.useMemo(() => {
      if (!onSelect) return undefined;

      return {
        onChange: (_: any, selected: T[]) => {
          setSelectedRows(selected);
          onSelect && onSelect(selected);
        },
        selectedRowKeys: selectedRows.map((r) => r.id),
        ...rowSelection,
      };
    }, [onSelect, rowSelection, selectedRows]);

    const handleRowClick = React.useCallback(
      (row: T) => {
        if (!onSelect) return {};

        return {
          onClick: () => {
            const isRowSelected = selectedRows.find((r) => r.id === row.id);
            const finalSelectedRows = isRowSelected
              ? selectedRows.filter((r) => r.id !== row.id)
              : rowSelection?.type === 'radio'
                ? [row]
                : selectedRows.concat(row);

            setSelectedRows(finalSelectedRows);
            onSelect(finalSelectedRows);
          },
        };
      },
      [onSelect, rowSelection?.type, selectedRows],
    );

    const pagination: TablePaginationConfig = useMemo(() => {
      return {
        ...basePagination,
        current: paging?.page,
        pageSize: paging?.size,
      };
    }, [paging?.page, paging?.size]);

    const handleChange = React.useCallback(
      (pagination: TablePaginationConfig, _: any, sorter: any) => {
        const _pagination = {
          current: pagination.current || 1,
          pageSize: pagination.pageSize || basePagination.defaultPageSize,
        };

        let _sort = [] as any;

        if (Object.keys(sorter).length > 0) {
          _sort =
            Array.isArray(sorter) && sorter.length > 0
              ? sorter.map(({ field, order }) => ({
                  name: field,
                  direction: order,
                }))
              : [{ name: sorter.field, direction: sorter.order }];
        }

        onChange && onChange({ pagination: _pagination, sort: _sort });
      },
      [onChange],
    );

    const resultColumns = useMemo(() => {
      return visibleColumns?.filter(
        (col) =>
          col.available &&
          col.visible &&
          !hasRequiredRoles(userRoles, col.restrictedRoles),
      );
    }, [userRoles, visibleColumns]);

    const chekedVisibleColumns = React.useMemo(() => {
      return {
        selectedRowKeys: resultColumns
          ? resultColumns.map((r) => r.dataIndex)
          : [],
        onSelect: (record: any, selected: any) => {
          const column = visibleColumns?.find(
            (col) => col.dataIndex === record.dataIndex,
          );

          setVisibleColumns((state) =>
            state?.map((col) =>
              col.dataIndex === column?.dataIndex
                ? { ...col, visible: selected }
                : col,
            ),
          );
        },
      };
    }, [resultColumns, visibleColumns]);

    const expandableConfig = useMemo(
      () =>
        expandable
          ? {
              expandIcon: ({ onExpand, record, expanded }: any) => (
                <Button
                  size="small"
                  icon={
                    <RightOutlined
                      style={{ fontSize: 12 }}
                      rotate={expanded ? 90 : 0}
                    />
                  }
                  onClick={(e) => onExpand(record, e)}
                />
              ),
              ...expandable,
            }
          : undefined,
      [expandable],
    );

    useEffect(() => {
      if (persistentId) {
        window.localStorage.setItem(
          `${persistentId}/visibleColumns`,
          JSON.stringify(
            visibleColumns?.map(({ dataIndex, visible }) => ({
              dataIndex,
              visible,
            })) || [],
          ),
        );
      }
    }, [persistentId, visibleColumns]);

    return (
      <>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {rightPanel}
            {hasSettings && (
              <Button
                icon={<SettingOutlined />}
                onClick={() => setSettingsVisible(true)}
              >
                Настройки колонок
              </Button>
            )}
          </Space>
          <Table<T>
            rowKey="id"
            columns={resultColumns}
            size="small"
            bordered={true}
            rowSelection={rowSelectionParams}
            rowClassName={onSelect && styles.rowSelectable}
            pagination={pagination}
            expandable={expandableConfig}
            onRow={handleRowClick}
            onChange={handleChange}
            {...props}
          />
        </Space>

        {hasSettings && (
          <Drawer
            open={settingsVisible}
            title="Настройки колонок"
            width="400"
            onClose={() => setSettingsVisible(false)}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Typography.Title level={5}>Отображение колонок</Typography.Title>
              <Table<TColumn<T>>
                rowKey="dataIndex"
                size="small"
                columns={[
                  {
                    dataIndex: 'title',
                    title: 'Колонка',
                    render: (title, record) =>
                      title || record.nameInTableSettings || null,
                  },
                ]}
                dataSource={visibleColumns}
                rowSelection={chekedVisibleColumns}
                pagination={false}
                bordered
              />
            </Space>
          </Drawer>
        )}
      </>
    );
  },
);

// export const BaseTable = React.memo(BaseTableComponent);
export const BaseTable = BaseTableComponent;
